import { OutlineTextFieldWidget, TextInputType } from '@tableau/widgets';
import React from 'react';
import { defaultMemoize } from 'reselect';

import {AuthenticationTypeResolutionService} from '../../authentication/services/AuthenticationTypeResolutionService';
import { Messages } from '../../codegen/Localize';
import { SiteRolePickerMenu } from '../../common/react/components/SiteRolePickerMenu';
import {getSessionManagementServiceInstance} from '../../sessionManagement/SessionManagementService';
import { AuthenticationProviderType } from '../../settings/siteAuthSettings/services/SiteAuthenticationSettingsService';
import { SiteRolePickerMenuHelper } from '../../siteManagement/helpers/SiteRolePickerMenuHelper';
import { SiteRoles } from '../../ts/ServerApi/types/ISiteRole';
import { AddLocalUserDialogType } from '../../userManagement/services/AddLocalUserAction';
import { SiteRoleOption, SiteRoleOptionsResource } from '../services/resources/SiteRoleOptions';
import { IFormModel } from './AddLocalUsersDialog';
import { SiteRoleInfoLink } from './SiteRoleInfoLink';
import { ISiteRoleChanges, SiteRolesPickerContainer } from './SiteRolesPickerContainer';

export interface NewLocalUserFormState {
}

export interface NewLocalUserFormProps {
  readonly availableQuota: number;
  readonly busy: boolean;
  readonly formModel: IFormModel;
  readonly addLocalUserDialogType: string;
  readonly siteRole: string;
  readonly sitePickerMenuDisabled: boolean;
  readonly displayNameChangeHandler: (value: string) => void;
  readonly emailChangeHandler: (value: string) => void;
  readonly passwordChangeHandler: (value: string) => void;
  readonly confirmPasswordChangeHandler: (value: string) => void;
  readonly siteRoleChangesHandler: (siteRoleChanges: ISiteRoleChanges) => void;
  readonly getSiteRoleOptionBySiteRoleHandler: (siteRole: string) => SiteRoleOption;
  readonly onOptionSelectedHandler: (siteRoleOption: SiteRoleOption) => void;
}

const getDisabledRoleOptionsHandler = defaultMemoize((availableQuota: number, siteRoleOptions: SiteRoleOption[]): ReadonlyArray<SiteRoleOption> => SiteRolePickerMenuHelper.getPickerMenuDropdownOptionDisabledOptions(availableQuota, siteRoleOptions));

export class NewLocalUserForm extends React.Component<NewLocalUserFormProps, NewLocalUserFormState> {

  private siteRolesOptionResource: SiteRoleOptionsResource = SiteRoleOptionsResource.instance;
  private roleOptions = false === getSessionManagementServiceInstance().isSessionServerAdmin() ? this.siteRolesOptionResource.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator) : this.siteRolesOptionResource.getAllSiteRoleOptions();

  public render() {
    return (
    <tbody className = "tb-newUserFlow-table">
      <tr>
        <td></td>
        <td className="tb-available-cell">
          <span className="tb-available-text"> {this.props.formModel.newUserFlowModel.usernameInfo} </span>
        </td>
      </tr>
      <tr>
        <td className="tb-text-input-label-cell">
          <label>{Messages.displayName_colon()} </label>
        </td>
        <td className="tb-settings-control tb-input-wide">
          <OutlineTextFieldWidget
                    handleChange={this.props.displayNameChangeHandler}
                    testId="display-name-input"
                    disabled={this.props.busy}
                    maxLength={100}
                    text={this.props.formModel.newUserFlowModel.displayName}/>
        </td>
      </tr>
      {this.showPasswordField()}
      {this.showConfirmPasswordField()}
      <tr>
        <td className="tb-text-input-label-cell">
          <label>{Messages.emailOptional_colon()}</label>
        </td>
        <td className="tb-settings-control tb-input-wide">
          <OutlineTextFieldWidget
                    handleChange={this.props.emailChangeHandler}
                    testId="add-local-user-email"
                    disabled={this.props.busy}
                    maxLength={100}
                    text={this.props.formModel.newUserFlowModel.email}/>
        </td>
      </tr>
      {this.showSitePickerMenu()}
      {this.showSiteRolesPicker()}
    </tbody>);
  }

  private showPasswordField(): JSX.Element {
    const authenticationProvider = this.props.formModel.authenticationProviderSelectorProperties.authenticationProvider;
    let shouldShowPasswordField = authenticationProvider.type === AuthenticationProviderType.default && !AuthenticationTypeResolutionService.isAuthenticationSaml();
    if (shouldShowPasswordField) {
      return (<tr>
                <td className="tb-text-input-label-cell">
                  <label>{Messages.password_colon()}</label>
                </td>
                <td className="tb-settings-control tb-input-wide">
                  <OutlineTextFieldWidget
                    type={TextInputType.Password}
                    handleChange={this.props.passwordChangeHandler}
                    testId="add-local-user-password"
                    disabled={this.props.busy}
                    maxLength={100}
                    text={this.props.formModel.newUserFlowModel.password}/>
                </td>
              </tr>);
    }
    return (null);
  }

  private showConfirmPasswordField(): JSX.Element {
    const authenticationProvider = this.props.formModel.authenticationProviderSelectorProperties.authenticationProvider;
    let shouldShowPasswordField = authenticationProvider.type === AuthenticationProviderType.default
      && !AuthenticationTypeResolutionService.isAuthenticationSaml();
    if (shouldShowPasswordField) {
      return(<tr>
              <td className="tb-text-input-label-cell">
                <label>{Messages.confirmPassword_colon()}</label>
              </td>
              <td className="tb-settings-control tb-input-wide">
                <OutlineTextFieldWidget
                  type={TextInputType.Password}
                  handleChange={this.props.confirmPasswordChangeHandler}
                  testId="add-local-user-confirm-password"
                  disabled={this.props.busy}
                  maxLength={100}
                  text={this.props.formModel.newUserFlowModel.confirmPassword}/>
              </td>
            </tr>);
    }
    return (null);
  }

  private showSiteRolesPicker(): JSX.Element {
    if (this.props.addLocalUserDialogType === AddLocalUserDialogType.SERVER) {
      return(<tr>
              <td colSpan={2}>
                <div className="tb-picker-container tb-picker-border">
                  <SiteRolesPickerContainer
                    changes={this.props.siteRoleChangesHandler}
                    busy={this.props.busy}
                  />
                </div>
              </td>
            </tr>);
    }
    return (null);
  }

  private showSitePickerMenu(): JSX.Element {
    if (this.props.addLocalUserDialogType === AddLocalUserDialogType.SITE) {
      return(<><tr>
              <td className="tb-react-text-input-label-cell">{Messages.siteRole_colon()}</td>
              <td className="tb-settings-control tb-react-input-wide">
              <div style={{ display: 'inline-flex' }}>
              <SiteRolePickerMenu
                  siteRoleOptions={this.roleOptions}
                  getSiteRoleOptionBySiteRole={this.props.getSiteRoleOptionBySiteRoleHandler}
                  onOptionSelected={this.props.onOptionSelectedHandler}
                  selectedRole={this.siteRolesOptionResource.getSiteRoleOptionBySiteRole(this.props.siteRole)}
                  isDisabled={this.props.busy || this.props.sitePickerMenuDisabled}
                  disabledRoleOptions={[...getDisabledRoleOptionsHandler(this.props.availableQuota, this.roleOptions)]}/>
              </div>
              </td>
            </tr>
            <tr>
              <td></td>
              <td><SiteRoleInfoLink/></td>
            </tr>
            </>);
    }
    return (null);
  }

}
